/* eslint-disable no-use-before-define */
import Swal from 'sweetalert2'
import * as signalR from '@microsoft/signalr'
import useJwt from '@/auth/jwt/useJwt'
import ability from '@/libs/acl/ability'
import { initialAbility } from '@/libs/acl/config'

let routerRef = null
let storeRef = null
let connection = null
let fallbackTimer = null
let hubRetryTimer = null
let activityPingTimer = null
let idleWarningTimer = null
let idleLogoutTimer = null
let installed = false
let checkingSession = false
let loggingOut = false
let activityPingInFlight = false
let warningVisible = false
let lastActivityAt = Date.now()
let lastActivityPingAt = 0
let pendingActivityAt = 0
let idleExpiresAtMs = 0
let idleTimeoutMs = 30 * 60 * 1000
let idleWarningMs = 5 * 60 * 1000
let policyUpdatedAtMs = 0
let realtimeConnected = false
let activeSessionKey = ''

const loginSessionsChangedListeners = new Set()
const realtimeStateListeners = new Set()

const fallbackIntervalMs = 60000
const activityPingIntervalMs = 5000
const activityStorageKey = 'hdg:office:last-user-activity-at'
const logoutStorageKey = 'hdg:office:logout-at'
const userDataStorageKey = 'userData'
const activityEvents = ['click', 'keydown', 'pointerdown', 'touchstart', 'wheel']

const hasToken = () => !!useJwt.getToken()

const normalizeUsername = value => (value || '').trim().toLowerCase()

const readTokenPayload = () => {
  const token = useJwt.getToken()
  if (!token) return null

  const parts = token.split('.')
  if (parts.length < 2) return null

  try {
    const encoded = parts[1].replace(/-/g, '+').replace(/_/g, '/')
    return JSON.parse(window.atob(encoded))
  } catch (error) {
    return null
  }
}

const currentSessionIdentity = () => {
  const payload = readTokenPayload()
  return {
    sid: payload?.sid || '',
    username: normalizeUsername(payload?.username),
  }
}

const currentSessionKey = () => {
  const identity = currentSessionIdentity()
  if (identity.sid || identity.username) return `${identity.sid}:${identity.username}`
  return useJwt.getToken() || ''
}

const isSameSessionPayload = payload => {
  if (!payload || typeof payload !== 'object') return false

  const current = currentSessionIdentity()
  const sid = payload.sid || payload.sessionId || payload.session?.sid
  const username = payload.username || payload.session?.username
  if (sid && current.sid) return sid === current.sid
  if (username && current.username) return normalizeUsername(username) === current.username
  return false
}

const notifyListeners = (listeners, payload) => {
  listeners.forEach(listener => {
    try {
      listener(payload)
    } catch (error) {
      // A page-level listener should not break the shared session manager.
    }
  })
}

const setRealtimeConnected = value => {
  if (realtimeConnected === value) return
  realtimeConnected = value
  notifyListeners(realtimeStateListeners, realtimeConnected)
}

const notifyLoginSessionsChanged = payload => {
  notifyListeners(loginSessionsChangedListeners, payload || {})
}

const safeRouteToLogin = reason => {
  if (!routerRef || routerRef.currentRoute.name === 'auth-login') return
  routerRef.replace({ name: 'auth-login', query: reason ? { reason } : {} }).catch(() => {})
}

const parseTimeMs = value => {
  if (!value) return 0
  const parsed = new Date(value).getTime()
  return Number.isNaN(parsed) ? 0 : parsed
}

const clearTimer = timer => {
  if (timer) window.clearTimeout(timer)
}

const closeIdleWarning = () => {
  if (!warningVisible) return
  warningVisible = false
  Swal.close()
}

const clearIdleTimers = () => {
  clearTimer(activityPingTimer)
  clearTimer(idleWarningTimer)
  clearTimer(idleLogoutTimer)
  activityPingTimer = null
  idleWarningTimer = null
  idleLogoutTimer = null
  closeIdleWarning()
}

const resetSessionRuntimeState = () => {
  lastActivityAt = Date.now()
  lastActivityPingAt = 0
  pendingActivityAt = 0
  idleExpiresAtMs = 0
  policyUpdatedAtMs = 0
  checkingSession = false
  activityPingInFlight = false
  clearIdleTimers()
}

const idleDeadlineMs = () => idleExpiresAtMs || lastActivityAt + idleTimeoutMs

const scheduleIdleTimers = () => {
  clearTimer(idleWarningTimer)
  clearTimer(idleLogoutTimer)
  idleWarningTimer = null
  idleLogoutTimer = null

  if (!hasToken() || loggingOut) return

  const now = Date.now()
  const deadline = idleDeadlineMs()
  const logoutDelay = deadline - now
  if (logoutDelay <= 0) {
    forceLogout('idle-timeout')
    return
  }

  const warningDelay = deadline - idleWarningMs - now
  if (idleWarningMs > 0) {
    idleWarningTimer = window.setTimeout(
      showIdleWarning,
      Math.max(warningDelay, 0),
    )
  }

  idleLogoutTimer = window.setTimeout(() => {
    forceLogout('idle-timeout')
  }, logoutDelay + 1000)
}

const writeSharedActivity = activityAt => {
  try {
    localStorage.setItem(activityStorageKey, JSON.stringify({
      at: activityAt,
      ...currentSessionIdentity(),
    }))
  } catch (error) {
    // Private browsing/storage failures should not block the local idle guard.
  }
}

const clearSharedActivity = () => {
  try {
    localStorage.removeItem(activityStorageKey)
  } catch (error) {
    // Storage cleanup is best effort; cookies are the authoritative login state.
  }
}

const writeSharedLogout = reason => {
  try {
    localStorage.setItem(logoutStorageKey, JSON.stringify({
      at: Date.now(),
      reason: reason || 'session-revoked',
      ...currentSessionIdentity(),
    }))
  } catch (error) {
    // Storage broadcast is best effort; current tab still logs out locally.
  }
}

const readSharedLogoutPayload = value => {
  try {
    return JSON.parse(value)
  } catch (error) {
    return null
  }
}

const sharedLogoutReason = payload => (payload && payload.reason ? payload.reason : 'session-revoked')

const readUserData = value => {
  if (!value) return null
  try {
    return JSON.parse(value)
  } catch (error) {
    return null
  }
}

const applyUserData = userData => {
  if (!userData) {
    ability.update(initialAbility)
    if (storeRef) storeRef.commit('app/UPDATE_USER_DATA', '')
    return
  }

  ability.update(userData.ability || initialAbility)
  if (storeRef) storeRef.commit('app/UPDATE_USER_DATA', userData)
}

const applyLocalActivity = (activityAt, broadcast) => {
  if (!hasToken() || loggingOut) return
  if (!Number.isFinite(activityAt)) return
  if (activityAt < lastActivityAt) return

  lastActivityAt = activityAt
  if (broadcast) pendingActivityAt = Math.max(pendingActivityAt, activityAt)
  idleExpiresAtMs = lastActivityAt + idleTimeoutMs
  if (broadcast) writeSharedActivity(lastActivityAt)
  closeIdleWarning()
  scheduleIdleTimers()
}

const applyServerSessionState = data => {
  if (!data) return

  const incomingPolicyUpdatedAtMs = parseTimeMs(data.policyUpdatedAt || data.policy?.updateAt || data.policy?.updatedAt)
  if (incomingPolicyUpdatedAtMs && policyUpdatedAtMs && incomingPolicyUpdatedAtMs < policyUpdatedAtMs) return
  if (incomingPolicyUpdatedAtMs) policyUpdatedAtMs = incomingPolicyUpdatedAtMs

  if (data.idleTimeoutSeconds) idleTimeoutMs = data.idleTimeoutSeconds * 1000
  if (data.idleWarningSeconds) idleWarningMs = data.idleWarningSeconds * 1000

  const session = data.session || data
  const serverIdleExpiresAt = parseTimeMs(session.idleExpiresAt)
  if (serverIdleExpiresAt) idleExpiresAtMs = serverIdleExpiresAt

  scheduleIdleTimers()
}

const isAuthoritativeSessionFailure = error => {
  const status = error && error.response ? error.response.status : 0
  return status === 401 || status === 403
}

const sessionFailureReason = (error, fallback) => {
  const data = error && error.response ? error.response.data : null
  if (data && typeof data === 'object' && data.reason) return data.reason
  return fallback
}

const sendActivityPing = async () => {
  if (!hasToken() || loggingOut || activityPingInFlight) return
  const activityAt = pendingActivityAt
  if (!activityAt) return
  if (Date.now() >= idleDeadlineMs()) {
    await forceLogout('idle-timeout')
    return
  }

  activityPingInFlight = true
  lastActivityPingAt = Date.now()

  try {
    const response = await useJwt.recordSessionActivity(activityAt)
    if (pendingActivityAt <= activityAt) pendingActivityAt = 0
    applyServerSessionState(response.data)
  } catch (error) {
    if (isAuthoritativeSessionFailure(error)) {
      await forceLogout(sessionFailureReason(error, 'session-invalid'))
    }
  } finally {
    activityPingInFlight = false
  }
}

const scheduleActivityPing = () => {
  if (!hasToken() || loggingOut || activityPingTimer) return
  const elapsed = Date.now() - lastActivityPingAt
  const delay = Math.max(activityPingIntervalMs - elapsed, 0)
  activityPingTimer = window.setTimeout(() => {
    activityPingTimer = null
    sendActivityPing()
  }, delay)
}

const markUserActivity = event => {
  if (!hasToken() || loggingOut) return
  if (event && event.isTrusted === false) return
  if (event?.target?.closest && event.target.closest('.swal2-container')) return
  const now = Date.now()
  if (now >= idleDeadlineMs()) {
    forceLogout('idle-timeout')
    return
  }

  applyLocalActivity(now, true)
  scheduleActivityPing()
}

const handleSharedActivity = event => {
  if (event.key !== activityStorageKey || !event.newValue) return
  try {
    const payload = JSON.parse(event.newValue)
    if (!isSameSessionPayload(payload)) return
    applyLocalActivity(Number(payload.at), false)
  } catch (error) {
    // Ignore legacy unscoped activity broadcasts.
  }
}

const handleSharedLogout = event => {
  if (event.key !== logoutStorageKey || !event.newValue) return
  const payload = readSharedLogoutPayload(event.newValue)
  if (!isSameSessionPayload(payload)) return
  forceLogout(sharedLogoutReason(payload), false)
}

const handleSharedUserData = event => {
  if (event.key !== userDataStorageKey) return
  if (!event.newValue) {
    if (!hasToken()) applyUserData(null)
    return
  }

  applyUserData(readUserData(event.newValue))
  startSessionManager()
}

async function showIdleWarning() {
  if (!hasToken() || loggingOut || warningVisible) return
  warningVisible = true
  const deadlineAtOpen = idleDeadlineMs()

  const result = await Swal.fire({
    title: 'Session expiring soon',
    text: 'You have been inactive. Stay signed in?',
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: 'Stay signed in',
    cancelButtonText: 'Logout',
    allowOutsideClick: false,
    allowEscapeKey: false,
    timer: Math.max(idleDeadlineMs() - Date.now(), 1000),
    timerProgressBar: true,
    customClass: {
      confirmButton: 'btn btn-primary',
      cancelButton: 'btn btn-outline-secondary ml-1',
    },
    buttonsStyling: false,
  })

  warningVisible = false

  if (!hasToken() || loggingOut) return
  if (result.isConfirmed) {
    markUserActivity()
    return
  }

  if (idleDeadlineMs() > deadlineAtOpen && Date.now() < idleDeadlineMs()) {
    scheduleIdleTimers()
    return
  }

  await forceLogout('idle-timeout')
}

export async function stopSessionManager() {
  activeSessionKey = ''
  resetSessionRuntimeState()

  if (fallbackTimer) {
    window.clearInterval(fallbackTimer)
    fallbackTimer = null
  }

  if (hubRetryTimer) {
    window.clearTimeout(hubRetryTimer)
    hubRetryTimer = null
  }

  clearIdleTimers()

  if (connection) {
    const activeConnection = connection
    connection = null
    setRealtimeConnected(false)
    try {
      await activeConnection.stop()
    } catch (error) {
      // SignalR stop can race with a closed socket; the local session is already being cleared.
    }
  }
}

export const isSessionRealtimeConnected = () => realtimeConnected

export const onSessionRealtimeStateChanged = listener => {
  if (typeof listener !== 'function') return () => {}
  realtimeStateListeners.add(listener)
  listener(realtimeConnected)
  return () => realtimeStateListeners.delete(listener)
}

export const onLoginSessionsChanged = listener => {
  if (typeof listener !== 'function') return () => {}
  loginSessionsChangedListeners.add(listener)
  return () => loginSessionsChangedListeners.delete(listener)
}

export async function forceLogout(reason, broadcast = true) {
  if (loggingOut) return
  loggingOut = true
  await stopSessionManager()
  if (broadcast) writeSharedLogout(reason)
  useJwt.removeToken()
  clearSharedActivity()
  ability.update(initialAbility)
  if (storeRef) storeRef.commit('app/UPDATE_USER_DATA', '')
  safeRouteToLogin(reason || 'session-revoked')
  loggingOut = false
}

const checkCurrentSession = async () => {
  if (!hasToken() || checkingSession) return
  const tokenAtStart = useJwt.getToken()
  checkingSession = true
  try {
    const response = await useJwt.currentSession()
    if (tokenAtStart !== useJwt.getToken()) return
    applyServerSessionState(response.data)
  } catch (error) {
    if (tokenAtStart !== useJwt.getToken()) return
    if (isAuthoritativeSessionFailure(error)) {
      await forceLogout(sessionFailureReason(error, 'session-invalid'))
    }
  } finally {
    checkingSession = false
  }
}

const ensureFallback = () => {
  if (fallbackTimer || !hasToken()) return
  fallbackTimer = window.setInterval(checkCurrentSession, fallbackIntervalMs)
}

const scheduleHubRetry = retry => {
  if (hubRetryTimer || !hasToken() || loggingOut) return
  hubRetryTimer = window.setTimeout(() => {
    hubRetryTimer = null
    retry()
  }, 5000)
}

const startHub = async () => {
  if (!hasToken() || connection) return

  if (hubRetryTimer) {
    window.clearTimeout(hubRetryTimer)
    hubRetryTimer = null
  }

  connection = new signalR.HubConnectionBuilder()
    .withUrl(useJwt.jwtConfig.sessionHubEndpoint, {
      accessTokenFactory: () => useJwt.getToken() || '',
      skipNegotiation: true,
      transport: signalR.HttpTransportType.WebSockets,
    })
    .withAutomaticReconnect([0, 2000, 10000, 30000])
    .build()

  connection.on('sessionRevoked', payload => {
    forceLogout(payload && payload.reason ? payload.reason : 'session-revoked')
  })

  connection.on('sessionConnected', payload => {
    setRealtimeConnected(true)
    applyServerSessionState(payload)
  })

  connection.on('sessionPolicyChanged', payload => {
    if (!isSameSessionPayload(payload)) return
    applyServerSessionState(payload)
  })

  connection.on('sessionsChanged', notifyLoginSessionsChanged)

  connection.onreconnected(() => {
    setRealtimeConnected(true)
    checkCurrentSession()
  })

  connection.onclose(() => {
    connection = null
    setRealtimeConnected(false)
    if (loggingOut || !hasToken()) return
    checkCurrentSession().finally(() => scheduleHubRetry(startHub))
  })

  try {
    await connection.start()
    setRealtimeConnected(true)
  } catch (error) {
    connection = null
    setRealtimeConnected(false)
    if (loggingOut || !hasToken()) return
    checkCurrentSession().finally(() => scheduleHubRetry(startHub))
  }
}

export const startSessionManager = () => {
  if (!hasToken()) return
  const sessionKey = currentSessionKey()
  const shouldCheckNow = activeSessionKey !== sessionKey
  if (shouldCheckNow) resetSessionRuntimeState()
  activeSessionKey = sessionKey

  scheduleIdleTimers()
  ensureFallback()
  if (shouldCheckNow) {
    checkCurrentSession().finally(startHub)
    return
  }

  startHub()
}

export const installSessionManager = (router, store) => {
  if (installed) return
  installed = true
  routerRef = router
  storeRef = store

  activityEvents.forEach(eventName => {
    window.addEventListener(eventName, markUserActivity, true)
  })

  window.addEventListener('storage', handleSharedActivity)
  window.addEventListener('storage', handleSharedLogout)
  window.addEventListener('storage', handleSharedUserData)
  window.addEventListener('focus', () => {
    checkCurrentSession()
  })

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return
    checkCurrentSession()
  })

  router.afterEach(to => {
    if (to.name === 'auth-login') {
      stopSessionManager()
      return
    }
    startSessionManager()
  })

  startSessionManager()
}
